import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  fetchModelPricing,
  updateModelPricing,
  deleteModelPricing,
} from '@/lib/businessValueApi';
import { BEDROCK_MODEL_ID_HINT, isLikelyBedrockModelId } from '@/lib/bedrockModel';
import { usePermissions } from '@/contexts/PermissionsContext';
import { permissionDeniedTitle } from '@/lib/permissionUi';
import { toast } from 'sonner';
import { Plus, Save, Trash2 } from 'lucide-react';

function createRow(overrides = {}) {
  const rowKey =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  return {
    rowKey,
    model_id: '',
    inputPrice: '',
    outputPrice: '',
    ...overrides,
  };
}

function perMillion(per1k) {
  const n = Number(per1k);
  if (Number.isNaN(n)) return '—';
  return (n * 1000).toFixed(2);
}

export default function ModelPricingSettingsPanel({ onSaved }) {
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('business-value', 'create');
  const canUpdate = hasPermission('business-value', 'update');
  const canDelete = hasPermission('business-value', 'delete');
  const canEditFields = canCreate || canUpdate;
  const [rows, setRows] = useState([createRow()]);
  const [globalInput, setGlobalInput] = useState('0.003');
  const [globalOutput, setGlobalOutput] = useState('0.015');
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [deletingKey, setDeletingKey] = useState(null);
  const [knownModelIds, setKnownModelIds] = useState([]);

  useEffect(() => {
    fetchModelPricing()
      .then((data) => {
        setGlobalInput(String(data.global_input_price_per_1k));
        setGlobalOutput(String(data.global_output_price_per_1k));
        const models = (data.models || []).map((m) =>
          createRow({
            model_id: m.model_id,
            inputPrice: String(m.llm_input_price_per_1k),
            outputPrice: String(m.llm_output_price_per_1k),
          })
        );
        setRows(models.length > 0 ? models : [createRow()]);
        setKnownModelIds(data.known_model_ids || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const updateRow = (index, field, value) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const handleSaveRow = async (index) => {
    if (!canUpdate && !canCreate) return;
    const row = rows[index];
    const modelId = (row.model_id || '').trim();
    if (!modelId) {
      toast.error('Model id is required');
      return;
    }
    if (!isLikelyBedrockModelId(modelId)) {
      toast.error(
        'Use the full Bedrock inference profile id from LLM usage (see dropdown suggestions)'
      );
      return;
    }
    setSavingKey(row.rowKey);
    try {
      await updateModelPricing(modelId, {
        llm_input_price_per_1k: Number(row.inputPrice),
        llm_output_price_per_1k: Number(row.outputPrice),
      });
      toast.success(`Pricing saved for ${modelId}`);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save model pricing');
    } finally {
      setSavingKey(null);
    }
  };

  const handleDeleteRow = async (index) => {
    const row = rows[index];
    const modelId = (row.model_id || '').trim();

    if (!modelId) {
      if (!canCreate) return;
      setRows((prev) => {
        const next = prev.filter((_, i) => i !== index);
        return next.length > 0 ? next : [createRow()];
      });
      return;
    }

    if (!canDelete) return;

    setDeletingKey(row.rowKey);
    try {
      await deleteModelPricing(modelId);
      toast.success(`Removed pricing for ${modelId}`);
      setRows((prev) => {
        const next = prev.filter((_, i) => i !== index);
        return next.length > 0 ? next : [createRow()];
      });
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete model pricing');
    } finally {
      setDeletingKey(null);
    }
  };

  return (
    <Card data-testid="bv-model-pricing-settings">
      <CardHeader>
        <CardTitle>Per-model LLM pricing</CardTitle>
        <CardDescription>
          Override global token rates for specific Bedrock model ids. The model id must match
          exactly what orchestrator usage records (same string as in LLM cost by model tooltips).
          Unlisted models use global defaults (${globalInput}/1K input · ${globalOutput}/1K output
          — ${perMillion(globalInput)}/M · ${perMillion(globalOutput)}/M on Anthropic pages).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">{BEDROCK_MODEL_ID_HINT}</p>
        {knownModelIds.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Models seen in telemetry: {knownModelIds.join(' · ')}
          </p>
        )}
        <datalist id="bv-known-bedrock-models">
          {knownModelIds.map((id) => (
            <option key={id} value={id} />
          ))}
        </datalist>
        {rows.map((row, index) => (
          <div
            key={row.rowKey}
            className="flex flex-wrap items-end gap-3 border-b pb-4 last:border-0"
          >
            <div className="min-w-[240px] flex-1">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Bedrock model id
              </label>
              <Input
                list="bv-known-bedrock-models"
                placeholder="us.anthropic.claude-sonnet-4-5-20250929-v1:0"
                value={row.model_id}
                disabled={loading || !canEditFields}
                onChange={(e) => updateRow(index, 'model_id', e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Input $/1K
                {row.inputPrice ? (
                  <span className="ml-1 font-normal text-muted-foreground">
                    (${perMillion(row.inputPrice)}/M)
                  </span>
                ) : null}
              </label>
              <Input
                type="number"
                min="0"
                step="0.0001"
                className="w-36"
                value={row.inputPrice}
                disabled={loading || !canEditFields}
                onChange={(e) => updateRow(index, 'inputPrice', e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Output $/1K
                {row.outputPrice ? (
                  <span className="ml-1 font-normal text-muted-foreground">
                    (${perMillion(row.outputPrice)}/M)
                  </span>
                ) : null}
              </label>
              <Input
                type="number"
                min="0"
                step="0.0001"
                className="w-36"
                value={row.outputPrice}
                disabled={loading || !canEditFields}
                onChange={(e) => updateRow(index, 'outputPrice', e.target.value)}
              />
            </div>
            <span title={permissionDeniedTitle(canUpdate || canCreate, 'save model pricing')}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={loading || savingKey === row.rowKey || (!canUpdate && !canCreate)}
                onClick={() => handleSaveRow(index)}
              >
                <Save className="mr-1 h-4 w-4" />
                Save
              </Button>
            </span>
            <span
              title={permissionDeniedTitle(
                row.model_id.trim() ? canDelete : canCreate,
                'delete model pricing',
              )}
            >
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={
                  loading
                  || deletingKey === row.rowKey
                  || (row.model_id.trim() ? !canDelete : !canCreate)
                }
                onClick={() => handleDeleteRow(index)}
                aria-label={`Delete pricing for ${row.model_id || 'row'}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </span>
          </div>
        ))}
        <span title={permissionDeniedTitle(canCreate, 'add model pricing')}>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={loading || !canCreate}
            onClick={() => setRows((prev) => [...prev, createRow()])}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add model
          </Button>
        </span>
      </CardContent>
    </Card>
  );
}
